import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getAdminId } from '../utils/getAdminId';
import { Printer, Download, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/Button';

export const InvoiceViewer: React.FC = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [invoice, setInvoice] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const adminId = getAdminId(user);

      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          invoice_items:invoice_items(
            id,
            product_id,
            description,
            quantity,
            unit_price,
            total_price,
            sku,
            color_name
          )
        `)
        .eq('id', id)
        .eq('admin_id', adminId)
        .single();

      if (error) throw error;

      // Derive Color and SKU live from the linked product so they show on every
      // invoice type, falling back to the stored line values when a line has no
      // product_id or the product/color lookup misses.
      const items = data?.invoice_items || [];
      const productIds = Array.from(
        new Set(
          items
            .map((item: any) => item.product_id)
            .filter((pid: string | null): pid is string => !!pid)
        )
      );

      const productById = new Map<string, { sku: string | null; color_id: string | null }>();
      const colorById = new Map<string, string>();

      if (productIds.length > 0) {
        const { data: products } = await supabase
          .from('products')
          .select('id, sku, color_id')
          .in('id', productIds);

        (products || []).forEach((p: any) =>
          productById.set(p.id, { sku: p.sku ?? null, color_id: p.color_id ?? null })
        );

        const colorIds = Array.from(
          new Set(
            (products || [])
              .map((p: any) => p.color_id)
              .filter((cid: string | null): cid is string => !!cid)
          )
        );

        if (colorIds.length > 0) {
          const { data: colors } = await supabase
            .from('product_colors')
            .select('id, name')
            .in('id', colorIds);

          (colors || []).forEach((c: any) => colorById.set(c.id, c.name));
        }
      }

      const enrichedItems = items.map((item: any) => {
        const product = item.product_id ? productById.get(item.product_id) : undefined;
        const derivedColorName = product?.color_id ? colorById.get(product.color_id) : undefined;
        return {
          ...item,
          sku: product?.sku ?? item.sku ?? null,
          color_name: derivedColorName ?? item.color_name ?? null,
        };
      });

      setInvoice({ ...data, invoice_items: enrichedItems });
    } catch (err: any) {
      console.error('Error fetching invoice:', err);
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 text-lg mb-4">{error || 'Invoice not found'}</p>
          <Button onClick={() => window.history.back()}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="no-print mb-6 flex justify-between items-center">
          <Button onClick={() => window.history.back()} variant="secondary">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex gap-3">
            <Button onClick={handlePrint} variant="primary">
              <Printer className="h-4 w-4 mr-2" />
              Print Invoice
            </Button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 print:shadow-none">
          <div className="border-b-2 border-gray-200 dark:border-gray-700 pb-6 mb-6">
            <div className="flex justify-between items-start">
              <div>
                {user?.logo_url && (
                  <img src={user.logo_url} alt="Company Logo" crossOrigin="anonymous" className="h-16 mb-4" style={{ maxWidth: 120, objectFit: 'contain' }} />
                )}
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  {user?.company_name || 'Company Name'}
                </h1>
                {user?.company_street ? (
                  <>
                    <p className="text-gray-600 dark:text-gray-400">{user.company_street}{user.company_house_number ? ` ${user.company_house_number}` : ''}</p>
                    <p className="text-gray-600 dark:text-gray-400">{user.company_postal_code} {user.company_city}</p>
                    {user.company_country && user.company_country !== 'NL' && (
                      <p className="text-gray-600 dark:text-gray-400">{user.company_country}</p>
                    )}
                  </>
                ) : (
                  <p className="text-gray-600 dark:text-gray-400">{user?.company_address}</p>
                )}
                <p className="text-gray-600 dark:text-gray-400">{user?.company_email}</p>
                <p className="text-gray-600 dark:text-gray-400">{user?.company_phone}</p>
                {(user?.vat_number || user?.company_tax_id) && (
                  <p className="text-gray-600 dark:text-gray-400">VAT: {user.vat_number || user.company_tax_id}</p>
                )}
                {user?.kvk_number && (
                  <p className="text-gray-600 dark:text-gray-400">KvK: {user.kvk_number}</p>
                )}
                {user?.iban && (
                  <p className="text-gray-600 dark:text-gray-400">IBAN: {user.iban}</p>
                )}
                {user?.bic && (
                  <p className="text-gray-600 dark:text-gray-400">BIC: {user.bic}</p>
                )}
              </div>
              <div className="text-right">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{invoice.is_pro_forma ? 'PRO FORMA' : 'INVOICE'}</h2>
                <p className="text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Invoice #:</span> {invoice.invoice_number}
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Date:</span>{' '}
                  {new Date(invoice.invoice_date).toLocaleDateString()}
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Due Date:</span>{' '}
                  {new Date(invoice.due_date).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Bill To:</h3>
            <p className="text-gray-900 dark:text-gray-100 font-medium">{invoice.customer_name}</p>
            {invoice.customer_address && (
              <p className="text-gray-600 dark:text-gray-400">{invoice.customer_address}</p>
            )}
            {invoice.customer_email && (
              <p className="text-gray-600 dark:text-gray-400">{invoice.customer_email}</p>
            )}
            {invoice.customer_vat_number && (
              <p className="text-gray-600 dark:text-gray-400">VAT: {invoice.customer_vat_number}</p>
            )}
          </div>

          <div className="mb-8 overflow-x-auto">
            <table className="w-full table-fixed border-collapse text-sm">
              <colgroup>
                <col className="w-[34%]" />
                <col className="w-[14%]" />
                <col className="w-[16%]" />
                <col className="w-[8%]" />
                <col className="w-[14%]" />
                <col className="w-[14%]" />
              </colgroup>
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/40 border-y border-gray-200 dark:border-gray-700">
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Description</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Color</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">SKU</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Qty</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Unit Price</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.invoice_items?.map((item: any) => (
                  <tr key={item.id} className="border-b border-gray-100 dark:border-gray-700/60 align-top">
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100 break-words">{item.description}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 break-words">{item.color_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono text-xs break-words">{item.sku || '—'}</td>
                    <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100 tabular-nums whitespace-nowrap">{item.quantity}</td>
                    <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100 tabular-nums whitespace-nowrap">
                      €{parseFloat(item.unit_price).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100 tabular-nums whitespace-nowrap">
                      €{parseFloat(item.total_price).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end mb-8">
            <div className="w-full max-w-xs">
              <div className="flex justify-between py-2 text-gray-700 dark:text-gray-300">
                <span>Subtotal:</span>
                <span className="tabular-nums">€{parseFloat(invoice.subtotal_amount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-2 text-gray-700 dark:text-gray-300">
                <span>Tax:</span>
                <span className="tabular-nums">€{parseFloat(invoice.tax_amount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-3 border-t-2 border-gray-200 dark:border-gray-700 font-bold text-lg text-gray-900 dark:text-gray-100">
                <span>Total:</span>
                <span className="tabular-nums">€{parseFloat(invoice.total_amount).toFixed(2)}</span>
              </div>
              <div className="mt-4">
                <span
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    invoice.payment_status === 'paid'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : invoice.payment_status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  }`}
                >
                  Status: {invoice.payment_status.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          {invoice.notes && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Notes:</h3>
              <p className="text-gray-600 dark:text-gray-400">{invoice.notes}</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media print {
          body {
            background: white !important;
          }
          .no-print {
            display: none !important;
          }
          .dark\\:bg-gray-800,
          .dark\\:bg-gray-900 {
            background: white !important;
          }
          .dark\\:text-gray-100,
          .dark\\:text-gray-400,
          .dark\\:text-gray-600 {
            color: black !important;
          }
          .dark\\:border-gray-700 {
            border-color: #e5e7eb !important;
          }
        }
      `}</style>
    </div>
  );
};
